import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getAuth, type Auth } from 'firebase/auth';
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  type Firestore,
} from 'firebase/firestore';
import type { FirebaseConfig } from './types';

let firebaseApp: FirebaseApp | null = null;
let firebaseAuth: Auth | null = null;
let firebaseFirestore: Firestore | null = null;

/**
 * Initialize Firebase app and auth.
 * Idempotent - safe to call multiple times.
 */
export function initFirebaseAuth(config: FirebaseConfig): { app: FirebaseApp; auth: Auth } {
  if (firebaseApp && firebaseAuth) {
    return { app: firebaseApp, auth: firebaseAuth };
  }

  firebaseApp = initializeApp(config);
  firebaseAuth = getAuth(firebaseApp);

  return { app: firebaseApp, auth: firebaseAuth };
}

/**
 * Get the initialized Auth instance.
 * Throws if not initialized.
 */
export function getAuthInstance(): Auth {
  if (!firebaseAuth) {
    throw new Error('Firebase Auth not initialized. Call initFirebaseAuth() first.');
  }
  return firebaseAuth;
}

/**
 * Get the initialized FirebaseApp instance.
 * Throws if not initialized.
 */
export function getAppInstance(): FirebaseApp {
  if (!firebaseApp) {
    throw new Error('Firebase App not initialized. Call initFirebaseAuth() first.');
  }
  return firebaseApp;
}

/**
 * Get the Firestore instance (lazily created on first use).
 *
 * Uses IndexedDB persistent local cache so that RBAC role permission
 * documents fetched while online remain available offline — an offline reload
 * still resolves permissions from cache instead of failing closed. Multi-tab
 * manager keeps the cache consistent across web-admin tabs. Falls back to
 * in-memory automatically where IndexedDB is unavailable.
 *
 * Throws if Firebase has not been initialized yet.
 */
export function getFirestoreInstance(): Firestore {
  if (!firebaseFirestore) {
    firebaseFirestore = initializeFirestore(getAppInstance(), {
      localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
    });
  }
  return firebaseFirestore;
}
